/* ---------------------------------------------------------------------------
   Capa HTTP de los catálogos ORDENADOS del sistema.

   Tres mantenedores del Super Admin comparten exactamente la misma forma
   (`id, nombre, orden`) y el mismo CRUD: contenidos del informe, contextos e
   ítems de revisión. Este módulo es esa forma, una sola vez.

   El recurso viaja como PARÁMETRO y no como tres módulos calcados: la
   diferencia entre los catálogos es el segmento de la URL y los rótulos, no
   el comportamiento. El estado vive en TanStack Query (ver
   `src/hooks/useCatalogo.ts`), no acá.

   OJO — `contenido-items` NO usa este módulo. Su borrado tiene una guarda de
   referencia (422 con `informes_count`) que los otros dos catálogos no tienen
   todavía, porque las tablas que los van a referenciar son de fases
   posteriores. Meter esa rama acá sería código muerto para dos de los tres
   consumidores; cuando `contenido-items` migre, se agrega entonces.
--------------------------------------------------------------------------- */

import { apiFetch, parseValidationErrors } from "./api";

/** Segmento de la API que identifica al catálogo (`/api/<recurso>`). */
export type RecursoDeCatalogo = "contextos" | "informe-revision-items";

export interface ItemDeCatalogo {
  id: number;
  nombre: string;
  /**
   * Posición en el catálogo. Es de SOLO LECTURA: el backend la calcula en el
   * alta (`max(orden) + 1`) y ni el alta ni la edición la aceptan del payload.
   */
  orden: number;
}

export interface ItemDeCatalogoInput {
  nombre: string;
}

/** Viene ordenado por `orden` y sin los borrados lógicamente. */
export async function fetchCatalogo(recurso: RecursoDeCatalogo): Promise<ItemDeCatalogo[]> {
  const { data } = await apiFetch<{ data: ItemDeCatalogo[] }>(`/api/${recurso}`);
  return data;
}

/**
 * Alta: agrega al final del catálogo.
 *
 * Si el nombre pertenece a un ítem borrado lógicamente, el backend lo RESTAURA
 * en vez de insertar otra fila —conservando su `id` y su `orden` original— y
 * responde 201 igual. Para la pantalla el resultado es el mismo (el ítem
 * existe), pero puede aparecer en el medio del listado y no al final: es la
 * misma fila de antes volviendo a su lugar.
 */
export async function crearItemRequest(
  recurso: RecursoDeCatalogo,
  input: ItemDeCatalogoInput,
): Promise<ItemDeCatalogo> {
  const { data } = await apiFetch<{ data: ItemDeCatalogo }>(`/api/${recurso}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

/** Renombrar. El ítem conserva su posición: `orden` no viaja. */
export async function actualizarItemRequest(
  recurso: RecursoDeCatalogo,
  id: number,
  input: ItemDeCatalogoInput,
): Promise<ItemDeCatalogo> {
  const { data } = await apiFetch<{ data: ItemDeCatalogo }>(`/api/${recurso}/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return data;
}

/** Borrado lógico: 204 sin cuerpo. */
export function eliminarItemRequest(
  recurso: RecursoDeCatalogo,
  id: number,
): Promise<void> {
  return apiFetch<void>(`/api/${recurso}/${id}`, { method: "DELETE" });
}

/** Primer mensaje de validación del campo `nombre` en un 422; null si no aplica. */
export function errorDeNombreDeItem(error: unknown): string | null {
  return parseValidationErrors(error)?.nombre ?? null;
}
